import Toast from 'react-native-toast-message';
import { AxiosError } from 'axios';
import {makeAutoObservable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';
import moment from 'moment-timezone';

import {
  ERROR_MESSAGE,
  PASSWORD_RESET_REQUEST,
  SUCCESS_CHECK,
} from '../components/ToastBottom';
import config from '../config';
import {AppRoute} from '../models/AppRoute';
import {Address, User} from '../models/mfl';
import {RefreshSessionResponse} from '../models/middleware';
import {navigate} from '../navigation/RootNavigation';
import apiService, {
  convertAppAddressToMFLAddress,
} from '../transport/apiService';
import { UserV2 } from '../transport/types/user.type';
import {
  getValueFromStorage,
  removeValueFromStorage,
  setValueToStorage,
} from '../utils/localStorage';
import {
  getCreateOrUpdateUserErrorMessages,
  hasWeakPasswordError,
} from '../utils/mfl';

import type {RootStore} from './rootStore';
import { AccountScreenAction, parseAccountScreenActionKey, getAccountScreenActionKey, isAccountScreenActionKey } from '../models/navigationTypes';

const SESSION_ID = 'SESSION_ID';
const SESSION_SECRET = 'SESSION_SECRET';

export class UserStore {
  rootStore: RootStore;
  // Session Detail
  sessionId: string = '';
  sessionSecret: string = '';
  sessionToken: string = '';
  // User detail
  id: string = ''; // MFL ID
  zohoId: string = '';
  firstName: string = '';
  lastName: string = '';
  phoneNumber: string = '';
  emailAddress: string = '';
  password: string = '';
  dateOfBirth?: Date;
  billingAddress?: Address;
  showWeakPasswordCheckbox = false;
  postLoginReturnToScreen: AppRoute | [AppRoute, {screen: AppRoute}] | null =
    null;
  formValues: any;

  /**
   * This will be set if user has checked opt in rewards when create account but zen account creation did not succeed
   */
  loyaltyPending: boolean = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);
  }

  get name() {
    return `${this.firstName} ${this.lastName}`;
  }

  reset = () => {};

  default = () => {
    runInAction(() => {
      this.sessionId = '';
      this.sessionSecret = '';
      this.sessionToken = '';
      this.firstName = '';
      this.lastName = '';
      this.phoneNumber = '';
      this.emailAddress = '';
      this.password = '';
      this.dateOfBirth = undefined;
      this.billingAddress = undefined;
      this.showWeakPasswordCheckbox = false;
      this.postLoginReturnToScreen = null;
      this.formValues = null;
    });
  };

  retrieveSessionInformation = async () => {
    try {
      let sessionSecret = await getValueFromStorage(SESSION_SECRET);
      let sessionId = await getValueFromStorage(SESSION_ID);
      if (sessionSecret && sessionId) {
        const sessionInfo: RefreshSessionResponse | null =
          await apiService.refreshSession(sessionId, sessionSecret);
        if (sessionInfo) await this.storeSessionInfo(sessionInfo);
        else this.logout();
      } else {
        console.debug('[retrieveSessionInformation]No session info found.');
      }
    } catch (error: any) {
      this.logout();
      console.log(
        'There was a problem retrieving session details',
        error.code,
        error,
      );
    }
  };

  isLoggedIn = () => !!this.sessionToken && !!this.sessionSecret;

  isUserMissingDetails = () =>
    !this.dateOfBirth ||
    isNaN(this.dateOfBirth.getTime()) ||
    this.dateOfBirth.getTime() === new Date('1900-01-01').getTime() ||
    this.firstName.trim().length === 0 ||
    this.lastName.trim().length === 0 ||
    this.phoneNumber.trim().length === 0 ||
    this.emailAddress.trim().length === 0 ||
    !this.billingAddress ||
    this.billingAddress.lines.length === 0;

  setPostLoginReturnToScreen = (
    screen: AppRoute | null,
    params?: {screen: AppRoute; params?: Record<string, string>},
  ) => {
    if (params && !!screen) {
      this.postLoginReturnToScreen = [screen, params];
    } else {
      this.postLoginReturnToScreen = screen;
    }
  };

  logout = async () => {
    await apiService.logout();

    await removeValueFromStorage(SESSION_ID);
    await removeValueFromStorage(SESSION_SECRET);

    this.default();

    // Generate a new device ID on logout, per MFL request.
    await this.rootStore.globalStore.resetDeviceId();

    await this.rootStore.logoutUser();

    return null;
  };

  checkEmail = async (email: string) => {
    return apiService
      .checkEmail(email)
      .then((data: any) => {
        return {
          canSignIn: data.can_sign_in,
          canSignUp: data.can_sign_up,
        };
      })
      .catch(err => console.log(err));
  };

  checkEmailNew = async (email: string) => {
    this.emailAddress = email;

    return apiService
      .checkEmail2(email)
      .then((data: any) => {
        return {
          canSignIn: data.can_sign_in,
          canSignUp: data.can_sign_up,
          existsInZen: data.exists_in_zen,
          firstName: data.first_name,
        };
      })
      .catch(err => console.log(err));
  };

  login = async (body: {email: string; password: string}) => {
    if (config.FF_LOYALTY_ENABLED) {
      try {
        const data = (await apiService.login2(body)) as any;
        console.log('Login v2 data', data);

        if (data.needToVerifyEmail) {
          // navigate to email verification screen

          runInAction(() => {
            this.emailAddress = body?.email;
            this.password = body?.password; // needed for /verify-email endpoint
          });

          navigate(AppRoute.ROOT, {
            screen: AppRoute.MAIN,
            params: {screen: AppRoute.ACCOUNT_EMAIL_VERIFICATION},
          });
        } else {
          await this.storeSessionInfo(data);
          await this.rootStore.login();
          if (this.isUserMissingDetails()) {
            navigate(AppRoute.ROOT, {
              screen: AppRoute.MAIN,
              params: {screen: AppRoute.ACCOUNT_COLLECT_MISSING_DETAILS},
            });
            return false;
          }

          return true;
        }
      } catch (err) {
        if (err instanceof AxiosError) {
          // TODO surface error details to user
        } else {
          console.error('login-v2 error', err);
        }
        return false;
      }
    } else {
      try {
        const data = await apiService.login(body);
        await this.storeSessionInfo(data);
        await this.rootStore.login();

        return true;
      } catch (err) {
        console.error('login error', err);
        return false;
      }
    }
  };

  verifyUserEmail = async (code: string) => {
    try {
      const data = await apiService.verifyEmail(
        this.emailAddress,
        this.password,
        code,
      );

      if (data) {
        await this.storeSessionInfo(data);
        await this.rootStore.initialiseUserData();

        if (this.isUserMissingDetails()) {
          navigate(AppRoute.ROOT, {
            screen: AppRoute.MAIN,
            params: {screen: AppRoute.ACCOUNT_COLLECT_MISSING_DETAILS},
          });
          return false;
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error('verifyUserEmail error', error);
      return false;
    }
  };

  afterLogin = (fallback?: AppRoute) => {
    if (this.postLoginReturnToScreen) {
      if (Array.isArray(this.postLoginReturnToScreen)) {
        navigate(...this.postLoginReturnToScreen);
      } else {
        navigate(this.postLoginReturnToScreen);
      }
      this.setPostLoginReturnToScreen(null);
    } else if (fallback) {
      navigate(fallback);
    } else {
      const navigationTarget =
        this.rootStore.shopSelectionStore?.getHomeAppRoute();
      navigate(navigationTarget);
    }
  };

  afterUpdateUser = () => {
    this.afterLogin();
  }

  storeSessionInfo = async (data: RefreshSessionResponse) => {
    // Store session details
    await setValueToStorage(SESSION_ID, data.id);
    await setValueToStorage(SESSION_SECRET, data.session_secret);
    runInAction(() => {
      this.sessionSecret = data.session_secret;
      this.sessionToken = data.session_token;
      this.sessionId = data.id;
    });
  };

  storeUserInfo = (data: {
    id: string; // MFL ID
    first_name: string;
    last_name: string;
    phone_number: string;
    email_address: string;
    birth_date: string;
    billing_address: Address;
    zohoId?: string;
  }) => {
    try {
      if (window?.$zoho?.salesiq?.visitor) {
        // Despite Zoho documentation which says we to use the .ready() method,
        // it doesn't work, so we ignore it and just set the values directly.
        window.$zoho.salesiq.visitor.email(data.email_address);
        window.$zoho.salesiq.visitor.contactnumber(data.phone_number);
        window.$zoho.salesiq.visitor.name(
          `${data.first_name} ${data.last_name}`,
        );
      }
    } catch (error) {
      console.error('Zoho integration failed', error);
    }
    const dateOfBirth = moment.tz(data.birth_date, 'Australia/Sydney').toDate();
    runInAction(() => {
      this.id = data.id;
      this.firstName = data.first_name;
      this.lastName = data.last_name;
      this.phoneNumber = data.phone_number;
      this.emailAddress = data.email_address;
      this.dateOfBirth = dateOfBirth;
      this.billingAddress = data.billing_address;
      this.zohoId = data.zohoId || '';
    });
  };

  retrieveUserInfo = async () => {
    try {
      const data = await apiService.getUserInfo();
      console.log('retrieveUserInfo data', data);
      if (data) {
        this.storeUserInfo(data);
        return data;
      }
      return null;
    } catch (error) {
      console.log('Unable to retrieve user info', error);
      return null;
    }
  };

  retrieveUserInfoV2 = async (options?: {retryable: boolean}) => {
    try {
      const user =  await apiService.getUserInfoV2(options)
      const dateOfBirth = isNaN(user.dateOfBirth.getTime()) ? '' : user.dateOfBirth.toISOString();
      this.storeUserInfo({
        id: user.id,
        zohoId: user.zohoId,
        first_name: user.firstName,
        last_name: user.lastName,
        phone_number: user.phoneNumber,
        email_address: user.email,
        birth_date: dateOfBirth,
        billing_address: user.billingAddress,
      });
    } catch (err) {
      console.error('Unable to retrieve user info', err);
      throw err;
    }
  }

  handleCreateUpdateUserError = (err: any, isCreate: boolean = false) => {
    const errors = err.response?.data?.errors;
    let errorMessage = `Unable to ${
      isCreate ? 'create' : 'update'
    } user. Please try again.`;

    let isWeakPassword = false; 

    if (errors) {
      isWeakPassword = hasWeakPasswordError(errors);
      if (!isWeakPassword) {
        errorMessage = getCreateOrUpdateUserErrorMessages(errors);
      }
    }
    
    // New error handling
    if (err?.response?.data?.status === 'PASSWORD_TOO_WEAK') {
      isWeakPassword = true;
    }

    if (isWeakPassword) {
      runInAction(() => (this.showWeakPasswordCheckbox = true));
      errorMessage = 'The password is too weak';
    }

    Toast.show({
      type: ERROR_MESSAGE,
      text1: errorMessage,
      visibilityTime: 8000,
    });
  };

  updateUserInfo = async (
    action: keyof typeof AccountScreenAction | AccountScreenAction = 'UNKNOWN',
    values: User,
    successCallback = () => {},
    errorCallback: (error?: {status: string, message: string}) => void = () => {},
    options?: {
      showSuccessToast?: boolean,
      verificationCode?: string,
    },
  ) => {
    const {showSuccessToast = true, verificationCode} = options || {};
    try {
      runInAction(() => {
        this.showWeakPasswordCheckbox = false;
      });
      const actionString = parseAccountScreenActionKey(action) || 'UNKNOWN';
      const response = await apiService.updateUserInfo(
        actionString, 
        values, 
        {
          verificationCode,
        }
      );
      this.storeUserInfo(response.data);
      if (showSuccessToast) {
        Toast.show({
          type: SUCCESS_CHECK,
          onHide: successCallback,
        });
      } else {
        successCallback();
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const e = error.response?.data;
        errorCallback({
          status: e?.status,
          message: e?.message,
        });
      } else {
        errorCallback();
      }
      this.handleCreateUpdateUserError(error);
    }
  };

  updateBillingAddress = async (address: Address) => {
    console.log('updateBillingAddress', address);
    return new Promise<boolean>((resolve, reject) => {
      this.updateUserInfo(
        'UPDATE_BILLING_ADDRESS',
        {
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.emailAddress,
          phoneNumber: this.phoneNumber,
          dateOfBirth: this.dateOfBirth,
          billingAddress: { ...address },
        },
        () => resolve(true),
        () => reject(),
        {
          showSuccessToast: false,
        }
      );
    });
  };

  /** This create account is for the loyalty work, capturing the form data
   * while the user verifies their phone number.
   */
  updateUserFormValues = (values: any) => {
    runInAction(() => {
      this.formValues = values;
    });
  };

  signUpUser = async (code: string) => {
    const selectedStore =
      this.rootStore.shopSelectionStore.getSelectedStoreSummary();
    if (!selectedStore) {
      console.error('signUpUser error: no selected store');
      return false;
    }
    try {
      const response = await apiService.createUserNew(
        this.formValues,
        {
          id: selectedStore.id,
          name: selectedStore.name,
        },
        !!this.formValues.optInRewards,
        code as any,
      );

      if (!response || !response.data) {
        console.error('signUpUser response error', response);
        return false;
      }

      const data = response.data;

      this.storeUserInfo(data);
      await this.storeSessionInfo(data.session);
      await this.rootStore.initialiseUserDataNonBlocking();
      if (data.rewardsUser) {
        // This won't be sent in the case of not opting in to rewards
        this.rootStore?.rewardsStore?.loadRewards(data.rewardsUser);
      } else if (this.formValues?.optInRewards) {
        /**
         * if opt in rewards is checked but zen account wasn't created. data.rewardsUser is null
         */
        runInAction(() => {
          this.loyaltyPending = true;
        });
      }

      return true;
    } catch (error) {
      console.error('signUpUser error', error);
      return false;
    }
  };

  createUser = async (
    values: any,
    successCallback = () => {},
    errorCallback = () => {},
  ) => {
    try {
      runInAction(() => {
        this.showWeakPasswordCheckbox = false;
      });
      const data: any = await apiService.createUser(values);
      this.storeUserInfo(data);
      await this.storeSessionInfo(data.session);
      await this.rootStore.initialiseUserData();
      Toast.show({
        onHide: successCallback,
      });
    } catch (error) {
      errorCallback();
      this.handleCreateUpdateUserError(error);
    }
  };

  deleteUser = async (nav?: () => void) => {
    try {
      await apiService.deleteUser(this.emailAddress, this.phoneNumber, this.lastName);
      Toast.show({
        onHide: nav ? () => nav() : undefined,
      });
    } catch (err: any) {
      Toast.show({
        type: ERROR_MESSAGE,
        text1: err.message || 'Something went wrong. Please try again later.',
        visibilityTime: 8000,
      });
      console.error('error deleting user', err);
      throw err;
    }
  };

  initiatePasswordReset = async (
    email: string,
    nav: {(): any; (): any; (): void},
  ) => {
    try {
      await apiService.initiatePasswordReset({email_address: email});
      Toast.show({
        type: PASSWORD_RESET_REQUEST,
        visibilityTime: 3000,
        onHide: nav || undefined,
      });
    } catch (err) {
      console.error('Password reset error', err);
    }
  };

  resetPassword = async (
    {password, passwordValidationBypass = false}: {password: string, passwordValidationBypass: boolean},
    token: any,
  ): Promise<{success: boolean, message?: string}> => {
    // Establish new session with token
    let sessionData: any;
    try {
      sessionData = await apiService.establishSessionWithToken(token)
    } catch (err) {
      return {
        success: false,
        message: 'Invalid password reset token. Please request a new password reset.',
      }
    }
    await this.storeSessionInfo(sessionData);

    // Get user details
    let userData: any;
    try {
      userData = await apiService.getUserInfo()
      this.storeUserInfo(userData);
    } catch (err) {
      return {
        success: false,
        message: 'Failed to update user. Please try again.',
      }
    }
    
    // Update user with new password
    try {
      await apiService.updateUserInfo(
        'RESET_PASSWORD',
        {
          ...userData, 
          password: password,
          bypass_compromised_password_validation: passwordValidationBypass,
        }, 
        {
          skipConvert: true,
        }
      );
      return {
        success: true,
      } 
    } catch (err) {
      if (err instanceof AxiosError) {
        if (err.response?.data?.status === 'PASSWORD_TOO_WEAK') {
          runInAction(() => (this.showWeakPasswordCheckbox = true));
          return {
            message: 'Please enter a strong password with a mix of letters, numbers and symbols',
            success: false,
          };
        }
        if (err.response?.data?.errors) {
          for (const error of err.response.data.errors) {
            switch (error.code.toLowerCase()) {
              case 'password_too_weak': {
                runInAction(() => (this.showWeakPasswordCheckbox = true));
                return {
                  message: 'Please enter a strong password with a mix of letters, numbers and symbols',
                  success: false,
                };
              }
            }
          }
        }
      }
      return {
        success: false,
        message: 'Failed to update password. Please try again.',
      }
    }
  };

  /**
   * Complete full sign up from email link
   * @param values
   * @param successCallback
   * @param errorCallback
   */
  rewardsFullSignup = async (
    values: any,
    successCallback = () => {},
    errorCallback = () => {},
  ) => {
    const billingAddress = convertAppAddressToMFLAddress(values.billingAddress);

    try {
      const response = await apiService.convertLiteUser({
        email: this.emailAddress,
        password: values.password,
        billing_address: billingAddress,
        bypass_compromised_password_validation: values.passwordValidationBypass,
      });

      runInAction(() => {
        this.password = values.password; // needed for /verify-email endpoint
      });
      await apiService.rewardsVerifyExistingUserEmail({
        emailAddress: this.emailAddress,
      });

      navigate(AppRoute.ROOT, {
        screen: AppRoute.MAIN,
        params: {screen: AppRoute.ACCOUNT_EMAIL_VERIFICATION},
      });
    } catch (error: any) {
      console.log('rewardsFullSignup error', error);

      if (error.response?.status === 404) {
        // unknown error (user not found in zen)
        errorCallback();
      } else {
        errorCallback();
        this.handleCreateUpdateUserError(error, true);
      }
    }
  };
}
